import { useAuth } from '../contexts/AuthContext';
import { authFetch, API_BASE } from './authFetch';

const REQUEST_TIMEOUT = 30000; // 30 seconds

// Audit log event types
export enum AuditEventType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  DOCUMENT_LOADED = 'DOCUMENT_LOADED',
  FIELD_UPDATED = 'FIELD_UPDATED',
  VALIDATION_SUBMITTED = 'VALIDATION_SUBMITTED',
  UNAUTHORIZED_ACCESS = 'UNAUTHORIZED_ACCESS',
  SESSION_TIMEOUT = 'SESSION_TIMEOUT',
  ERROR_OCCURRED = 'ERROR_OCCURRED',
}

export interface AuditLogEntry {
  eventType: AuditEventType;
  userId: string;
  userEmail: string;
  timestamp: string;
  details: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

class SecurityService {
  private auditLogs: AuditLogEntry[] = [];

  /**
   * Authenticated fetch wrapper with timeout.
   * Accepts a full URL (for backward compat) or a relative path.
   * authFetch handles Bearer token + x-tenant-id — no manual header construction needed.
   */
  async secureRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    // Extract path relative to API_BASE (authFetch prepends API_BASE internally)
    const path = url.startsWith(API_BASE) ? url.slice(API_BASE.length) : url;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await authFetch(path, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Log security audit events
   */
  logAuditEvent(
    eventType: AuditEventType,
    userId: string,
    userEmail: string,
    details: Record<string, any> = {}
  ): void {
    const auditEntry: AuditLogEntry = {
      eventType,
      userId,
      userEmail,
      timestamp: new Date().toISOString(),
      details,
      ipAddress: this.getClientIP(),
      userAgent: navigator.userAgent,
    };

    this.auditLogs.push(auditEntry);
    
    // In production, this would send to a secure logging service
    console.log('Audit Log:', auditEntry);
    
    // Send to backend audit service (implement based on your backend)
    this.sendAuditLogToBackend(auditEntry).catch(error => {
      console.error('Failed to send audit log:', error);
    });
  }

  /**
   * Get client IP address (best effort)
   */
  private getClientIP(): string {
    // This is a simplified implementation
    // In production, you might get this from a backend service
    return 'client-ip-not-available';
  }

  /**
   * Send audit log to backend service.
   * Currently logs locally only — backend /api/audit-logs endpoint is not yet implemented.
   * When the endpoint is added, uncomment the authFetch call below.
   */
  private async sendAuditLogToBackend(_auditEntry: AuditLogEntry): Promise<void> {
    // Backend audit-logs endpoint not yet implemented — skip network call.
    // Audit entries are already stored in this.auditLogs and logged to console.
  }

  /**
   * Get audit logs (for debugging/admin purposes)
   */
  getAuditLogs(): AuditLogEntry[] {
    return [...this.auditLogs];
  }

  /**
   * Clear local audit logs
   */
  clearAuditLogs(): void {
    this.auditLogs = [];
  }

  /**
   * Sanitize sensitive data from objects
   */
  sanitizeData(data: any): any {
    const sensitiveFields = ['password', 'token', 'secret', 'key', 'authorization'];
    
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    const sanitized = { ...data };
    
    for (const field of sensitiveFields) {
      if (field in sanitized) {
        sanitized[field] = '[REDACTED]';
      }
    }

    return sanitized;
  }
}

// Export singleton instance
export const securityService = new SecurityService();

// Hook for using security service with auth context
export const useSecurityService = () => {
  const { user } = useAuth();

  const secureApiCall = async <T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> => {
    return securityService.secureRequest<T>(url, options);
  };

  const logUserAction = (
    eventType: AuditEventType,
    details: Record<string, any> = {}
  ) => {
    if (user) {
      securityService.logAuditEvent(
        eventType,
        user.localAccountId || user.homeAccountId || 'unknown',
        user.username || 'unknown',
        securityService.sanitizeData(details)
      );
    }
  };

  return {
    secureApiCall,
    logUserAction,
    sanitizeData: securityService.sanitizeData,
  };
};